import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { create } from "zustand";
import { useApi } from "~/api/provider";
import { useCurrentWorkspace, useViewer } from "~/api/queries";

export interface ConversationTurn {
  id: string;
  prompt: string;
  at: string;
  status: "thinking" | "proposed" | "working" | "done" | "failed" | "dismissed";
  answer: string | null;
  saved: boolean;
  pileId: string | null;
  dispatchId: string | null;
  workerName: string | null;
  workerResult: string | null;
  route: string | null;
  plan: {
    kind: "answer" | "gather" | "action" | "delegate";
    brief: string;
    workerId: string | null;
    title: string;
  } | null;
}
export interface WorkspaceConversation {
  id: string;
  title: string;
  version: number;
  kept: boolean;
  archivedAt: string | null;
  updatedAt: string;
  sourcesAvailable: boolean;
  workerStatus?: string | null;
  turns: ConversationTurn[];
  audit: { action: string; at: string; actorId: string }[];
}
export interface ConversationLibrary {
  conversations: Pick<
    WorkspaceConversation,
    "id" | "title" | "kept" | "archivedAt" | "updatedAt"
  >[];
  prompts: {
    conversationId: string;
    archivedAt: string | null;
    id: string;
    prompt: string;
    at: string;
  }[];
  savedActions: { conversationId: string; id: string; prompt: string; at: string }[];
}
export interface WorkspaceWorker {
  id: string;
  name: string;
  kind: "ai" | "ide";
  available: boolean;
  workspacePath: string | null;
}

// Drafts belong to the signed-in person AND workspace. Never carry an instruction across a workspace switch.
export const useWorkspaceComposer = create<{
  drafts: Record<string, string>;
  submission: { scope: string; prompt: string; id: string } | null;
  setDraft: (scope: string, text: string) => void;
  submit: (scope: string) => void;
  consumed: () => void;
}>((set, get) => ({
  drafts: {},
  submission: null,
  setDraft: (scope, text) => set((s) => ({ drafts: { ...s.drafts, [scope]: text } })),
  submit: (scope) => {
    const prompt = get().drafts[scope]?.trim();
    if (prompt) set({ submission: { scope, prompt, id: crypto.randomUUID() } });
  },
  consumed: () => set({ submission: null }),
}));

export function useConversationScope() {
  const { data: user } = useViewer();
  const { data: workspace } = useCurrentWorkspace();
  return user && workspace ? `${user.id}:${workspace.id}` : null;
}
export function useConversationRpc() {
  const api = useApi();
  return async <T>(method: string, body: unknown = {}): Promise<T> => {
    const result = (await api.research.post(`workspace-thinker.conversations/${method}`, body)) as {
      ok: boolean;
      data: T;
      message?: string;
    };
    if (!result.ok)
      throw new Error(result.message ?? "The conversation request could not be completed.");
    return result.data;
  };
}
export function useConversationLibrary(enabled: boolean) {
  const scope = useConversationScope();
  const rpc = useConversationRpc();
  return useQuery({
    queryKey: ["workspace-conversations", scope, "library"],
    queryFn: () => rpc<ConversationLibrary>("list"),
    enabled: !!scope && enabled,
  });
}
export function useWorkspaceWorkers(enabled: boolean) {
  const scope = useConversationScope();
  const rpc = useConversationRpc();
  return useQuery({
    queryKey: ["workspace-conversations", scope, "workers"],
    queryFn: () => rpc<WorkspaceWorker[]>("workers"),
    enabled: !!scope && enabled,
    refetchInterval: enabled ? 30000 : false,
  });
}
export function useWorkspaceConversation(id: string | null) {
  const scope = useConversationScope();
  const rpc = useConversationRpc();
  return useQuery({
    queryKey: ["workspace-conversations", scope, id],
    enabled: !!scope && !!id,
    queryFn: async () => {
      const row = await rpc<WorkspaceConversation>("get", { id });
      const worker = row.turns.find((t) => t.status === "working" && t.dispatchId);
      return worker && row.sourcesAvailable
        ? rpc<WorkspaceConversation>("sync", { id, version: row.version, turnId: worker.id })
        : row;
    },
    refetchInterval: (q) =>
      q.state.data?.turns.some(
        (t) =>
          (t.status === "working" || t.status === "thinking") &&
          Date.now() - new Date(t.at).getTime() < 3600000,
      )
        ? 5000
        : false,
  });
}
export function useConversationMutation() {
  const scope = useConversationScope();
  const rpc = useConversationRpc();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ method, ...body }: { method: string } & Record<string, unknown>) =>
      rpc<WorkspaceConversation>(method, body),
    onSuccess: (row) => {
      client.setQueryData(["workspace-conversations", scope, row.id], row);
      void client.invalidateQueries({ queryKey: ["workspace-conversations", scope, "library"] });
      void client.invalidateQueries({ queryKey: ["work", "piles"] });
    },
    onError: () => {
      void client.invalidateQueries({ queryKey: ["workspace-conversations", scope] });
    },
  });
}
